import { Link } from "react-router-dom";
import { Bell, IdCard, Briefcase, Building2, ChevronRight, Crown, Download, FileText, HelpCircle, Info, LayoutGrid, Lock, LogOut, Mail, Pencil, Phone, ScanLine, ShieldCheck, Target, Trash2 } from "lucide-react";
import { ProfileAvatar } from "@/components/ProfileAvatar";
import { AvatarCrown, PremiumBadge } from "@/components/PremiumBadge";

const Card = ({ title, children, className = "", testid }) => (
  <section className={`t-card p-5 ${className}`} data-testid={testid}>
    {title && <h2 className="text-[17px] font-bold mb-4">{title}</h2>}
    {children}
  </section>
);

const Detail = ({ icon: Icon, label, value }) => (
  <div className="rounded-[14px] bg-[#F8FAFC] px-4 py-3 flex items-center gap-3 min-w-0">
    <span className="w-9 h-9 rounded-[10px] bg-white border border-[#E2E8F0] flex items-center justify-center shrink-0"><Icon className="w-4 h-4 text-[#475569]" /></span>
    <span className="min-w-0"><span className="block text-[12px] font-semibold uppercase tracking-wide text-[#64748B]">{label}</span><span className="block text-[15px] font-semibold truncate">{value || "—"}</span></span>
  </div>
);

const Tile = ({ icon: Icon, label, desc, to, onClick, fg, bg, testid }) => {
  const cls = "group t-card p-4 flex items-start gap-3 text-left transition-[transform,box-shadow,border-color] hover:-translate-y-0.5 hover:border-[#0000EC]/30 hover:shadow-[0_12px_28px_-18px_rgba(0,0,236,.45)]";
  const body = (
    <>
      <span className="w-11 h-11 rounded-[12px] flex items-center justify-center shrink-0" style={{ background: bg }}><Icon className="w-5 h-5" style={{ color: fg }} /></span>
      <span className="flex-1 min-w-0"><span className="block text-[15px] font-bold">{label}</span><span className="block text-[13px] text-[#64748B]">{desc}</span></span>
      <ChevronRight className="w-5 h-5 text-[#CBD5E1] group-hover:text-[#0000EC] transition-colors" />
    </>
  );
  return to ? <Link to={to} className={cls} data-testid={testid}>{body}</Link> : <button onClick={onClick} className={cls} data-testid={testid}>{body}</button>;
};

const LinkRow = ({ icon: Icon, label, to, testid }) => (
  <Link to={to} className="flex items-center gap-3 px-2 h-11 rounded-xl text-[14px] font-semibold text-[#334155] hover:bg-[#F8FAFC]" data-testid={testid}>
    <Icon className="w-4 h-4 text-[#64748B]" /><span className="flex-1">{label}</span><ChevronRight className="w-4 h-4 text-[#CBD5E1]" />
  </Link>
);

export default function ProfileDesktop({ user, me, usage, sub, isPremium, canAdmin, canDelete, bizCat, busy, onExport, onLogout, onDelete }) {
  const limit = usage?.freeScanLimit ?? 0;
  const pct = usage && !usage.isPremium && limit ? Math.min(100, (usage.scansUsed / limit) * 100) : 100;
  return (
    <div className="px-10 py-8 max-w-[1400px] mx-auto fade-up" data-testid="profile-desktop">
      <section className="t-card overflow-hidden">
        <div className="h-28 bg-gradient-to-r from-[#0000EC] to-[#4338CA] relative">
          <div className="absolute inset-0 opacity-[0.15] [background-image:radial-gradient(white_1px,transparent_1px)] [background-size:20px_20px]" />
        </div>
        <div className="relative z-10 px-6 pb-6 flex items-end gap-5 -mt-12">
          <span className="rounded-full ring-[6px] ring-white bg-white"><AvatarCrown show={isPremium}><ProfileAvatar name={user?.name} size={104} /></AvatarCrown></span>
          <div className="flex-1 min-w-0 pb-1 pt-14">
            <div className="flex items-center gap-2">
              <h1 className="text-[28px] font-extrabold truncate" data-testid="profile-name">{user?.name}</h1>
              {isPremium && <PremiumBadge testid="profile-premium-badge" />}
            </div>
            <p className="text-[15px] text-[#475569] truncate">{[user?.designation, user?.companyName].filter(Boolean).join(" · ") || "Add your business details"}</p>
            <div className="flex flex-wrap gap-4 mt-2 text-[14px] text-[#64748B]">
              <span className="inline-flex items-center gap-1.5" data-testid="profile-email"><Mail className="w-4 h-4" />{user?.email}</span>
              {user?.phone && <span className="inline-flex items-center gap-1.5" data-testid="profile-phone"><Phone className="w-4 h-4" />{user.phone}</span>}
            </div>
          </div>
          <Link to="/profile/edit" className="t-btn t-btn-outline h-11 px-5 text-[14px]" data-testid="profile-edit"><Pencil className="w-4 h-4" />Edit Profile</Link>
        </div>
      </section>

      <div className="grid grid-cols-3 gap-6 mt-6">
        <div className="col-span-2 space-y-6">
          <Card title="Account details" testid="profile-business">
            <div className="grid grid-cols-2 gap-3">
              <Detail icon={Mail} label="Email" value={user?.email} />
              <Detail icon={Phone} label="Mobile" value={user?.phone} />
              <Detail icon={Building2} label="Company" value={user?.companyName} />
              <Detail icon={Briefcase} label="Designation" value={user?.designation} />
              <Detail icon={LayoutGrid} label="Business category" value={bizCat} />
              <Detail icon={ShieldCheck} label="Account" value={me?.canAccessAdmin ? "Owner / Admin" : "Member"} />
            </div>
          </Card>
          <div>
            <h2 className="text-[17px] font-bold mb-3">Quick actions</h2>
            <div className="grid grid-cols-2 gap-4">
              <Tile icon={Bell} label="Notifications" desc="Choose which alerts you receive" to="/notifications/settings" fg="#EA580C" bg="#FFF7ED" testid="profile-notifications" />
              <Tile icon={Target} label="Goals" desc="Daily networking goals & streaks" to="/goals" fg="#7C3AED" bg="#F5F3FF" testid="profile-goals" />
              <Tile icon={Download} label={busy ? "Exporting…" : "Export contacts"} desc="Download all contacts as CSV" onClick={onExport} fg="#059669" bg="#ECFDF5" testid="profile-export" />
              {canAdmin && <Tile icon={ShieldCheck} label="Admin Panel" desc="Users, plans, banners & broadcasts" to="/admin" fg="#0000EC" bg="#E5E5FD" testid="profile-admin-panel" />}
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <section className={`rounded-[20px] p-5 text-white relative overflow-hidden ${isPremium ? "bg-gradient-to-br from-[#4F46E5] to-[#7C3AED]" : "bg-gradient-to-br from-[#0000EC] to-[#4338CA]"}`} data-testid="profile-plan-card">
            <div className="absolute -right-10 -top-10 w-36 h-36 rounded-full bg-white/10" />
            <p className="relative flex items-center gap-2 text-[13px] font-semibold text-white/85"><Crown className="w-4 h-4" />Current Plan</p>
            <p className="relative text-[26px] font-extrabold mt-1" data-testid="profile-current-plan">{!sub ? "—" : isPremium ? "Premium" : "Free"}</p>
            <div className="relative mt-3">
              <div className="flex justify-between text-[13px] text-white/85"><span className="inline-flex items-center gap-1.5"><ScanLine className="w-4 h-4" />Scan usage</span><span className="font-bold" data-testid="profile-scan-usage">{usage?.isPremium ? "Unlimited" : usage ? `${usage.scansUsed} / ${limit}` : "—"}</span></div>
              <div className="mt-2 h-2 rounded-full bg-white/20 overflow-hidden"><div className="h-full bg-white rounded-full" style={{ width: `${pct}%` }} /></div>
            </div>
            {!isPremium && <Link to="/subscription" className="relative mt-4 w-full t-btn bg-white text-[#0000EC] h-11 text-[15px]" data-testid="profile-upgrade">Upgrade to Premium</Link>}
            {sub?.isAdminEntitlement && <p className="relative mt-3 text-[12px] text-white/80" data-testid="profile-admin-premium-note">Premium is included permanently while this account remains the designated admin.</p>}
          </section>
          <Card title="Support & legal">
            <LinkRow icon={IdCard} label="Digital Business Card" to="/dbc" testid="profile-dbc" />
            <LinkRow icon={HelpCircle} label="Help & Support" to="/legal/support" testid="profile-support" />
            <LinkRow icon={Info} label="About TOTO" to="/legal/about" testid="profile-about" />
            <LinkRow icon={Lock} label="Privacy Policy" to="/legal/privacy" testid="profile-privacy" />
            <LinkRow icon={FileText} label="Terms of Service" to="/legal/terms" testid="profile-terms" />
          </Card>
          <Card title="Session">
            <button onClick={onLogout} className="w-full t-btn t-btn-ghost border border-[#E2E8F0] h-11 text-[14px]" data-testid="profile-logout"><LogOut className="w-4 h-4" />Log out</button>
            {canDelete && <button onClick={onDelete} className="w-full mt-2 h-11 rounded-[14px] text-[14px] font-semibold text-[#DC2626] hover:bg-[#FEF2F2] inline-flex items-center justify-center gap-2" data-testid="profile-delete"><Trash2 className="w-4 h-4" />Delete account</button>}
            {me?.canDeleteAccount === false && <p className="mt-3 text-[12px] text-[#64748B]" data-testid="profile-admin-delete-protection">The designated admin account cannot be deleted.</p>}
            <p className="mt-3 text-[12px] text-[#64748B] flex items-center gap-1.5"><ShieldCheck className="w-4 h-4 text-[#059669]" />Your contacts and card images stay private.</p>
          </Card>
        </div>
      </div>
    </div>
  );
}
